import { useState, useRef, useEffect } from "react";
import {
  RiDownloadCloud2Line,
  RiFolder3Line,
  RiLineChartLine,
  RiMenuLine,
  RiBankLine,
  RiTrophyLine,
  RiArrowDownSLine,
  RiFileListLine,
  RiCopperCoinLine
} from "react-icons/ri";
import { Link, useLocation } from "react-router-dom";
import { Tooltip } from "@mui/material";
import { useOnboard } from "../../../hooks/OnboardContext";
import { i18n } from "../../../localization/i18n";
import { MenuButton, MenuItemContainer, SubMenuWrapper, SubMenuItem } from "../styles";
import { usePrinters } from "../../../hooks/PrintersContext";

interface MenuListProps {
  extendedMode: boolean;
  setExtendedMode?: (val: boolean) => void;
}

export const LicenseeMenuList = ({ extendedMode, setExtendedMode }: MenuListProps) => {
  const { licensee, planHasExpired } = useOnboard();
  const { userHasPrinter } = usePrinters();
  const location = useLocation();

  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);

  const tooltipProps = {
    tooltip: {
      sx: {
        backgroundColor: "var(--dark-gray)",
        color: "var(--white)",
        fontSize: "14px",
        fontWeight: "500",
        padding: "8px 14px",
        borderRadius: "6px",
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)"
      }
    },
    arrow: {
      sx: {
        color: "var(--dark-gray)"
      }
    }
  };

  const handleSubmenuToggle = (id: string) => {
    if (!extendedMode && setExtendedMode) {
      setExtendedMode(true);
      setOpenSubmenu(id);
    } else {
      setOpenSubmenu((prev) => (prev === id ? null : id));
    }
  };

  const isFinancialActive = location.pathname.includes("/financial");
  const isFinancialOpen = openSubmenu === "financial" && extendedMode;

  const AccordionSubmenu = ({
    isOpen,
  }: {
    isOpen: boolean;
  }) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState(0);

    useEffect(() => {
      if (isOpen && contentRef.current) {
        setHeight(contentRef.current.scrollHeight);
      } else {
        setHeight(0);
      }
    }, [isOpen]);

    return (
      <SubMenuWrapper isOpen={isOpen} contentHeight={height}>
        <div ref={contentRef}>
          <SubMenuItem isActive={location.pathname === "/financial/reports"}>
            <Link to={!planHasExpired && userHasPrinter ? "/financial/reports" : "/dashboard"}>
              <span className="sub-icon"><RiLineChartLine size={24} /></span>
              {`${i18n.t("dashboard.header")}`}
            </Link>
          </SubMenuItem>
          <SubMenuItem isActive={location.pathname === "/financial/bills"}>
            <Link to={!planHasExpired && userHasPrinter ? "/financial/bills" : "/dashboard"}>
              <span className="sub-icon"><RiFileListLine size={24} /></span>
              {`${i18n.t("bills.bills")}`}
            </Link>
          </SubMenuItem>
          <SubMenuItem isActive={location.pathname === "/financial/transactions"}>
            <Link to={!planHasExpired && userHasPrinter ? "/financial/transactions" : "/dashboard"}>
              <span className="sub-icon"><RiCopperCoinLine size={24} /></span>
              FixCoins
            </Link>
          </SubMenuItem>
        </div>
      </SubMenuWrapper>
    );
  };

  return (
    <>
      <MenuItemContainer>
        <Link to={!planHasExpired ? "/dashboard" : "/dashboard"}>
          <Tooltip 
            title={extendedMode ? "" : `${i18n.t("sidebar.reports")}`} 
            placement="right" 
            arrow 
            disableHoverListener={extendedMode}
            componentsProps={tooltipProps}
          >
            <span style={{ display: 'block' }}>
              <MenuButton 
                extendedMode={extendedMode} 
                disabled={planHasExpired || licensee?.is_blocked}
                isActive={location.pathname === "/dashboard" || location.pathname === "/"}
              >
                <div className="content">
                  <RiLineChartLine size={24} />
                  <span>{`${i18n.t("sidebar.reports")}`}</span>
                </div>
              </MenuButton>
            </span>
          </Tooltip>
        </Link>
      </MenuItemContainer>

      <MenuItemContainer>
        <Link to={!planHasExpired ? "/solutions" : "/dashboard"}>
          <Tooltip 
            title={extendedMode ? "" : `${i18n.t("sidebar.solutions")}`} 
            placement="right" 
            arrow 
            disableHoverListener={extendedMode}
            componentsProps={tooltipProps}
          >
            <span style={{ display: 'block' }}>
              <MenuButton 
                extendedMode={extendedMode} 
                disabled={planHasExpired || licensee?.is_blocked}
                isActive={location.pathname.includes("/solutions")}
              >
                <div className="content">
                  <RiDownloadCloud2Line size={24} />
                  <span>{`${i18n.t("sidebar.solutions")}`}</span>
                </div>
              </MenuButton>
            </span>
          </Tooltip>
        </Link>
      </MenuItemContainer>

      <MenuItemContainer>
        <Link to={!planHasExpired ? "/orders" : "/dashboard"}>
          <Tooltip 
            title={extendedMode ? "" : `${i18n.t("sidebar.requests")}`} 
            placement="right" 
            arrow 
            disableHoverListener={extendedMode}
            componentsProps={tooltipProps}
          >
            <span style={{ display: 'block' }}>
              <MenuButton 
                extendedMode={extendedMode} 
                disabled={planHasExpired || licensee?.is_blocked}
                isActive={location.pathname.includes("/orders")}
              >
                <div className="content">
                  <RiMenuLine size={24} />
                  <span>{`${i18n.t("sidebar.requests")}`}</span>
                </div>
              </MenuButton>
            </span>
          </Tooltip>
        </Link>
      </MenuItemContainer>

      {userHasPrinter && (
        <MenuItemContainer>
            <Tooltip 
              title={extendedMode ? "" : `${i18n.t("sidebar.financial")}`} 
              placement="right" 
              arrow 
              disableHoverListener={extendedMode}
              componentsProps={tooltipProps}
            >
              <span style={{ display: 'block' }}>
                <MenuButton 
                  extendedMode={extendedMode} 
                  disabled={planHasExpired || licensee?.is_blocked}
                  isActive={isFinancialActive}
                  onClick={() => handleSubmenuToggle("financial")}
                >
                  <div className="content">
                    <RiBankLine size={24} />
                    <span>{`${i18n.t("sidebar.financial")}`}</span>
                  </div>
                  <RiArrowDownSLine
                    className="arrow"
                    size={20}
                    style={{
                      transform: isFinancialOpen ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                  />
                </MenuButton>
              </span>
            </Tooltip>
            <AccordionSubmenu isOpen={isFinancialOpen} />
        </MenuItemContainer>
      )}

      {licensee?.situation === "active" && (
        <>
          {!planHasExpired && (
            <MenuItemContainer>
              <a
                href="https://drive.google.com/drive/folders/12ouIWR70G6D1YOIiq4e8nstEZZ_tirVf?usp=sharing"
                target="_blank"
                rel="noreferrer"
              >
                <Tooltip 
                  title={extendedMode ? "" : `${i18n.t("sidebar.files")}`} 
                  placement="right" 
                  arrow 
                  disableHoverListener={extendedMode}
                  componentsProps={tooltipProps}
                >
                  <span style={{ display: 'block' }}>
                <MenuButton
                  extendedMode={extendedMode}
                  disabled={planHasExpired || licensee?.is_blocked}
                >
                  <div className="content">
                    <RiFolder3Line size={24} />
                    <span>{`${i18n.t("sidebar.files")}`}</span>
                  </div>
                </MenuButton>
                  </span>
                </Tooltip>
              </a>
            </MenuItemContainer>
          )}

          <MenuItemContainer>
            <Link to={!planHasExpired ? "/success" : ""}>
              <Tooltip 
                title={extendedMode ? "" : `${i18n.t("sidebar.success")}`} 
                placement="right" 
                arrow 
                disableHoverListener={extendedMode}
                componentsProps={tooltipProps}
              >
                <span style={{ display: 'block' }}>
              <MenuButton 
                extendedMode={extendedMode} 
                disabled={planHasExpired || licensee?.is_blocked}
                isActive={location.pathname.includes("/success")}
              >
                <div className="content">
                  <RiTrophyLine size={24} />
                  <span>{`${i18n.t("sidebar.success")}`}</span>
                </div>
              </MenuButton>
                </span>
              </Tooltip>
            </Link>
          </MenuItemContainer>
        </>
      )}
    </>
  );
};

